import {isUndefined} from 'lodash';
import React, {FC, Key, useRef} from 'react';

import {unwrapNode} from '../../../../../libs/shared/helpers/src/browser/domHelpers';
import {usePrevious} from '../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {CommentComposerFormattingLinkEditor} from './commentComposerFormattingLinkEditor';
import {CommentFormattingOptionsEnum, trackManualFormattingEvent} from './commentComposerFormattingTracking';

/*
 * Props.
 */

interface CommentComposerFormattingLinkToolbarProps {
  conversationId: number | undefined;
  linkElement: HTMLAnchorElement;
  onRequestClose: Handler;
}

/*
 * Component.
 */

export const CommentComposerFormattingLinkToolbar: FC<CommentComposerFormattingLinkToolbarProps> = (
  props,
) => {
  const {conversationId, linkElement, onRequestClose} = props;
  // The link editor is an uncontrolled component, so we need to re-mount it when we hover over a new link.
  const key = useUniqueKeyForValue(linkElement);

  const onUrlSelect: HandlerOf<string | undefined> = (newUrl) => {
    if (isUndefined(newUrl)) {
      removeLink(linkElement);
    } else {
      linkElement.setAttribute('href', newUrl);
    }

    // Request for the toolbar to be dismissed.
    onRequestClose();

    trackManualFormattingEvent(conversationId, CommentFormattingOptionsEnum.LINK);
  };

  return (
    <CommentComposerFormattingLinkEditor
      key={key}
      shouldFocus={false}
      initialUrl={linkElement.href}
      onUrlSelect={onUrlSelect}
      onRequestClose={onRequestClose}
    />
  );
};

/*
 * Hooks.
 */

function useUniqueKeyForValue(value: unknown): Key {
  const prevValue = usePrevious(value);
  const keyRef = useRef(0);

  // Simply increment a number every time we see a new value.
  if (prevValue !== value) {
    keyRef.current += 1;
  }

  return keyRef.current;
}

/*
 * Helpers.
 */

function removeLink(linkElement: Element): void {
  unwrapNode(linkElement);
}
